import React from 'react'

import { useUIPaginationState } from '../../providers/UIPaginationStateProvider'

const INITIAL_STATE = {
  index: 0,
  total: 0,
  paginated: false,
  visible: 0
}

const useUIPagination = (elems = [], PAGE_SIZE, key, manual) => {
  const { state, update, move, reset, moveTo } = useUIPaginationState()

  const [pageSize, _setPageSize] = React.useState(PAGE_SIZE)
  const [fullPagination, setFullPagination] = React.useState({
    page: 0,
    per_page: PAGE_SIZE,
    total: 0,
    urlFunc: () => ''
  })

  const setPageSize = React.useCallback(
    newSize => {
      _setPageSize(newSize)
      reset(key)
    },
    [reset, key]
  )

  // Pull values from provider, uses defaults on first render
  const { index = 0, total, paginated, visible } = React.useMemo(() => {
    if (key in state) {
      return state[key]
    }

    return INITIAL_STATE
  }, [key, state])

  // Calculate new values
  const newTotal = React.useMemo(() => elems.length, [elems.length])
  const newPaginated = React.useMemo(() => {
    return total > pageSize
  }, [total, pageSize])
  const newVisible = React.useMemo(() => {
    return Math.min((index + 1) * pageSize, total)
  }, [index, pageSize, total])

  // Update provider
  React.useEffect(() => {
    update(key, newTotal, newPaginated, newVisible)
  }, [update, key, newTotal, newPaginated, newVisible])

  // Trim visible based on state
  const visibleElems = React.useMemo(() => {
    if (manual) {
      return elems
    }
    return elems.slice(index * pageSize, (index + 1) * pageSize)
  }, [manual, elems, index, pageSize])

  // Returns a function to move back or null when not allowed
  const prev = React.useMemo(() => {
    if (!paginated || index === 0) {
      return null
    }

    return () => move(key, false)
  }, [paginated, index, move, key])

  // Returns a function to move forward or null if not allowed
  const next = React.useMemo(() => {
    if (!paginated) {
      return null
    }

    const lastPageIdx = Math.ceil(total / pageSize) - 1
    if (index >= lastPageIdx) {
      return null
    }

    return () => move(key, true)
  }, [paginated, total, pageSize, index, move, key])

  const updatePagination = React.useCallback(
    (pagination, urlFunc) => {
      setFullPagination({ ...pagination, urlFunc })

      const { total } = pagination
      const paginated = total > pageSize
      update(key, total, paginated, newVisible)
    },
    [pageSize, update, key, newVisible]
  )

  const updateIndex = React.useCallback(
    index => {
      moveTo(key, index)
    },
    [moveTo, key]
  )

  return {
    index,
    total,
    paginated,
    visible,
    prev,
    next,
    visibleElems,
    updatePagination,
    setPageSize,
    updateIndex,
    fullPagination
  }
}

export default useUIPagination
